/**
 * Development-only diagnostics sink for browser-side debugging.
 *
 * @remarks
 * Engineering principles prohibit direct `console.*` usage in production
 * code. This helper preserves development visibility by publishing
 * structured browser events without writing to the console.
 */

export type DevDiagnosticLevel = 'debug' | 'info' | 'warn' | 'error'

/**
 * Structured development diagnostic payload.
 *
 * @public
 */
export interface DevDiagnosticEntry {
  level: DevDiagnosticLevel
  message: string
  context?: Record<string, unknown>
  timestamp: string
}

const DEV_DIAGNOSTIC_EVENT = 'defconai:dev-diagnostic'

/**
 * Publish a development-only diagnostic event to the browser.
 *
 * @param level - Diagnostic severity.
 * @param message - Short machine-readable diagnostic message.
 * @param context - Optional structured context for debugging.
 */
export function emitDevDiagnostic(
  level: DevDiagnosticLevel,
  message: string,
  context?: Record<string, unknown>,
): void {
  if (!import.meta.env.DEV) {
    return
  }

  const detail: DevDiagnosticEntry = {
    level,
    message,
    context,
    timestamp: new Date().toISOString(),
  }

  window.dispatchEvent(new CustomEvent<DevDiagnosticEntry>(DEV_DIAGNOSTIC_EVENT, { detail }))
}
